// Public catalogue helpers — the data behind the signed-out storefront: the
// category CHIPS, the search box, and the difficulty control. Pure functions, no
// Vue, no fetch, so the browse logic is unit-testable.
//
// Why chips are built at SUBJECT level, not domain:
// the picker groups by domain, which for the public catalogue produces "Public
// decks (22) / Healthcare (2)" — an artefact of the synthetic "public-decks"
// node, not a taxonomy. A visitor thinks in "Geography", "First aid", "Driver
// knowledge" — those are SUBJECTS (category[1]). So the chips come off the
// deck's own category path rather than the picker tree.

// deck.category is [domainSlug, subjectSlug, topicSlug]. Prefer the subject;
// fall back to the domain for a deck that only classifies one level deep.
export function chipSlugOf(deck) {
  const path = Array.isArray(deck?.category) ? deck.category.filter(Boolean) : [];
  return path[1] || path[0] || null;
}

export function prettifySlug(slug) {
  return String(slug || "")
    .split("-").filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

// The chip row: "All decks" + one chip per subject present in the catalogue,
// biggest first (a chip with 1 deck behind it is noise). Uncategorised decks are
// reachable via "All decks" and don't earn a chip of their own.
export function buildChips(decks = []) {
  const counts = new Map();
  for (const d of decks) {
    const slug = chipSlugOf(d);
    if (!slug) continue;
    counts.set(slug, (counts.get(slug) || 0) + 1);
  }
  const chips = [...counts.entries()]
    .map(([slug, count]) => ({ slug, label: prettifySlug(slug), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return [{ slug: null, label: "All decks", count: decks.length }, ...chips];
}

// Free-text search across the fields a visitor would actually type: the deck
// name, its blurb/tagline, its tags and its category path. Case- and
// punctuation-insensitive, matches on every word typed (AND), so "geo capitals"
// finds "World Geography - Capitals".
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();

export function deckHaystack(deck) {
  return norm([
    deck?.topic,
    deck?.description,
    deck?.cover?.tagline,
    ...(Array.isArray(deck?.tags) ? deck.tags : []),
    ...(Array.isArray(deck?.category) ? deck.category.map(prettifySlug) : [])
  ].filter(Boolean).join(" "));
}

export function searchDecks(decks = [], query = "") {
  const terms = norm(query).split(" ").filter(Boolean);
  if (terms.length === 0) return decks;
  return decks.filter((d) => {
    const hay = deckHaystack(d);
    return terms.every((t) => hay.includes(t));
  });
}

// The visitor picks a BAND ("Beginner"), not a raw 1-5 level — and a band spans
// two levels, so it must filter on the whole range. (Mapping "Beginner" to the
// single value 1 would silently hide every level-2 deck.) The bands mirror the
// card's own labelling in DeckCoverCard.
export const DIFFICULTY_BANDS = [
  { key: "beginner", label: "Beginner", levels: [1, 2] },
  { key: "intermediate", label: "Intermediate", levels: [3] },
  { key: "advanced", label: "Advanced", levels: [4, 5] }
];

// A deck matches a band if ANY of its items sit in it — a deck is a range, not a
// point, which is the same semantics the existing facet filter uses.
export function matchesDifficulty(deck, bandKey) {
  if (!bandKey) return true;
  const band = DIFFICULTY_BANDS.find((b) => b.key === bandKey);
  if (!band) return true;
  const ds = Array.isArray(deck?.difficulties) ? deck.difficulties : [];
  return ds.some((d) => band.levels.includes(Number(d)));
}

// Only offer a band the catalogue can actually satisfy — an option that always
// returns "no results" is worse than no option.
export function difficultyOptions(decks = []) {
  return DIFFICULTY_BANDS.filter((b) => decks.some((d) => matchesDifficulty(d, b.key)))
    .map(({ key, label }) => ({ key, label }));
}

// The one place the three controls compose. All AND, so order is irrelevant.
export function applyCatalogFilters(decks = [], { chip = null, query = "", difficulty = null } = {}) {
  return searchDecks(decks, query)
    .filter((d) => (chip ? chipSlugOf(d) === chip : true))
    .filter((d) => matchesDifficulty(d, difficulty));
}
